import { SystemStyleObject } from '@chakra-ui/react'

import { fontFamilies } from '../../theme/fontFamilies'
import type { ColorScheme } from '../../types'

const tokens = {
  dark: {
    bg: 'bg.card',
    focusRing: 'interactive.focus-ring',
    iconColor: 'icon.secondary',
    selectedBg: 'bg.table-row-selected',
    title: 'text.secondary',
    timestamp: 'text.secondary',
    hoverBg: 'bg.table-row-hover',
  },
  light: {
    bg: 'light.bg.card',
    focusRing: 'light.interactive.focus-ring',
    iconColor: 'light.icon.secondary',
    selectedBg: 'light.bg.table-row-selected',
    title: 'light.text.secondary',
    timestamp: 'light.text.secondary',
    hoverBg: 'light.bg.table-row-hover',
  },
}

export const feedItemStyles = (
  colorScheme: ColorScheme = 'dark',
  isSelected = false,
  isInteractive = false,
  align: 'start' | 'center' = 'start',
  isCompact = false,
  cursor?: SystemStyleObject['cursor']
): Record<string, SystemStyleObject> => {
  const t = tokens[colorScheme]
  return {
    // Always a Box — a nested trailing action (e.g. an ActionMenu trigger)
    // inside a root <button> would be invalid HTML, so the interactive
    // treatment lives on the inner button instead.
    container: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: align === 'center' ? 'center' : 'flex-start',
      bg: isSelected ? t.selectedBg : t.bg,
      border: '1px solid',
      borderColor: isSelected ? t.focusRing : 'transparent',
      borderRadius: 0,
      cursor: cursor ?? (isInteractive ? 'pointer' : 'default'),
      pt: isCompact ? '8px' : '10px',
      pr: '10px',
      // Centered single-line rows (e.g. Saved Filter rows) need symmetric
      // vertical padding for the centering to read.
      pb: isCompact ? '8px' : align === 'center' ? '10px' : '12px',
      pl: '10px',
      transition: 'background 150ms ease, border-color 150ms ease',
      userSelect: isInteractive ? 'none' : 'auto',
      w: 'full',
      ...(isInteractive && {
        _hover: {
          bg: isSelected ? t.selectedBg : t.hoverBg,
        },
      }),
    },
    // Reset-styled button wrapping the icon + content columns when the item
    // is interactive; the visual chrome stays on the container above.
    innerButton: {
      WebkitAppearance: 'none',
      appearance: 'none',
      alignItems: align === 'center' ? 'center' : 'flex-start',
      bg: 'transparent',
      border: 'none',
      color: 'inherit',
      cursor: cursor ?? 'pointer',
      display: 'flex',
      flex: 1,
      flexDirection: 'row',
      font: 'inherit',
      m: 0,
      minW: 0,
      p: 0,
      textAlign: 'left',
      _focusVisible: {
        outline: '2px solid',
        outlineColor: t.focusRing,
        outlineOffset: '2px',
      },
    },
    trailingAction: {
      alignItems: 'center',
      alignSelf: 'center',
      display: 'flex',
      flexShrink: 0,
      ml: '8px',
    },
    iconCol: {
      display: 'flex',
      alignItems: align === 'center' ? 'center' : 'flex-start',
      flexShrink: 0,
      width: isCompact ? '42px' : '50px',
    },
    iconFrame: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '30px',
      height: '30px',
      color: t.iconColor,
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      // Compact rows sit the note directly under the title; the default keeps
      // feed entries airier. Only the gap changes — the title/note line heights
      // stay on the Design Library type scale (Scale Card), which owns them.
      gap: isCompact ? 0 : '2px',
    },
    headerRow: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
    },
    // Holds the avatar beside the message so `headerRow`'s space-between still
    // pushes the status pill to the far edge.
    titleGroup: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: '8px',
      flex: 1,
      minWidth: 0,
    },
    // Optical alignment with the message's first line rather than the centre of
    // a message that has wrapped to several lines.
    avatar: {
      display: 'flex',
      flexShrink: 0,
      marginTop: '2px',
    },
    title: {
      fontSize: '16px',
      fontFamily: fontFamilies.soehne,
      fontWeight: '400',
      lineHeight: '1.625',
      color: t.title,
      flex: 1,
    },
    timestamp: {
      fontSize: '12px',
      fontFamily: fontFamilies.soehneMono,
      fontWeight: '400',
      color: t.timestamp,
      lineHeight: '1.583',
    },
    // Same type treatment as the below-title note, but pinned to the end of the
    // header row (right-aligned) for timeline feeds that keep the timestamp on
    // the title line. titleGroup's flex:1 pushes it to the far edge; it never
    // shrinks or wraps.
    timestampInline: {
      fontSize: '12px',
      fontFamily: fontFamilies.soehneMono,
      fontWeight: '400',
      color: t.timestamp,
      lineHeight: '1.583',
      flexShrink: 0,
      whiteSpace: 'nowrap',
      ml: '8px',
    },
    children: {
      color: t.timestamp,
      fontFamily: fontFamilies.soehne,
      fontSize: '16px',
      fontWeight: '400',
      lineHeight: 'normal',
    },
  }
}
